import store from "redux/store";
import axios from "axios";

// utilities
import { checkMoney, transaction } from "common/money";

//api
import api from "api/api";

/** Class representing a slot. */
class Slot {
	#id;
	#destination;
	#price;

	/**
	 * Create a slot.
	 * @param {number} _destination The id of the destination airport.
	 * @param {number} _price The price of the slot.
	 * @param {object} [_options={}] Optional. Other slot data.
	 */
	constructor(_destination, _price, _options = {}) {
		this.#id = _options.id;
		this.#destination = _destination;
		this.#price = _price;

		const nonStandardOptions = _options || {};

		if (_options.includeNonStandardProperties) {
			delete nonStandardOptions.id;
			delete nonStandardOptions.destination;
			delete nonStandardOptions.price;
			delete nonStandardOptions.sell;
			delete nonStandardOptions.update;
			delete nonStandardOptions.assignSchedule; //? remove ???
			delete nonStandardOptions.assignFlight; //? remove ???
			delete nonStandardOptions.includeNonStandardProperties;

			Object.entries(nonStandardOptions).forEach(([key, value]) => {
				this[key] = value;
			});
		}
	}

	get id() {
		return this.#id;
	}

	set id(_id) {
		throw new Error(`Id is read-only! Received ${_id}`);
	}

	get destination() {
		return this.#destination;
	}

	set destination(d) {
		throw new Error(`Destination is read-only! Received ${d}`);
	}

	get price() {
		return this.#price;
	}

	set price(p) {
		throw new Error(`Price is read-only! Received ${p}`);
	}

	/** This method deletes non-standard Slot properties such as distance and amount */
	removeNonStandardProperties() {
		const nonStandardProperties = ["amount", "distance"];
		nonStandardProperties.forEach(property => delete this[property]);
	}

	/** Updates the slot in the redux store */
	update() {
		this.removeNonStandardProperties();
		store.dispatch({ type: "UPDATE_SLOT", newSlot: this });
	}

	/**
	 * A method for selling a slot.
	 */
	async sell() {
		const currLanguage = store.getState().lang;
		if (this.#price > 0) {
			const confirmation = window.confirm(
				currLanguage === "PL"
					? "Czy chcesz sprzedać jeden slot za 75% jego wartości?"
					: "Are you sure you want to sell one slot for 75% of its value?"
			);

			if (!confirmation) return;
		}

		try {
			await axios.delete(`${api}/slot/${this.id}`);
			store.dispatch({ type: "DELETE_SLOT", slotId: this.#id });
		} catch (e) {
			if (e.response.status === 404) {
				alert(currLanguage === "PL" ? "Nie znaleziono slotu!" : "The slot wasn't found!");
				return;
			}

			if (e.response.status === 409) {
				alert(
					currLanguage === "PL"
						? "Nie można sprzedać, ponieważ slot jest w użyciu!"
						: "Cannot sell, because slot has already been used!"
				);
				return;
			}

			console.error(e);
			alert(currLanguage === "PL" ? "Wystąpił błąd!" : "An error has occurred");
			return;
		}

		this.#price > 0 && (await transaction("add", Math.floor(0.75 * this.#price), 1));
	}

	/**
	 * Only checks locally (that is client side)
	 */
	checkIfSlotUsed() {
		const allUsedSlots = store.getState().flights.map(f => f.slotId);
		return allUsedSlots.includes(this.id);
	}

	/**
	 * A method used to convert a class instance into an object. Also outputs private properties!
	 * @return {Object} A class instance converted to an object
	 */
	toObject() {
		const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(this));

		let instanceAsObject = keys.reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, {});

		instanceAsObject = Object.keys(this).reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, instanceAsObject);

		delete instanceAsObject.toObject;
		delete instanceAsObject.constructor;
		return instanceAsObject;
	}

	static async buy(destination, price, amount = 1) {
		const currLanguage = store.getState().lang;
		const requiredMoney = price * amount;

		try {
			if (requiredMoney > 0 && !(await checkMoney(requiredMoney, currLanguage))) {
				return;
			}
		} catch (e) {
			return;
		}

		try {
			const {
				data: { createdSlots, createdDemands }
			} = await axios.post(`${api}/slot`, { destination, price, amount });

			const newSlots = [];
			for (let i = 0; i < amount; i++) {
				const newSlot = new Slot(destination, price, { id: createdSlots[i] });
				newSlots.push(newSlot);
			}

			store.dispatch({ type: "ADD_SLOTS", slots: newSlots });
			store.dispatch({ type: "CREATE_DEMANDS", payload: createdDemands });

			// TODO: Integrate handling money stuff inside the API
			if (requiredMoney > 0) {
				await transaction("subtract", amount * price, 3);
			}

			alert(currLanguage === "PL" ? "Zaktualizowano listę posiadanych slotów." : "Updated the list of owned slots.");
			return true;
		} catch (error) {
			alert(currLanguage === "PL" ? "Wystąpił błąd!" : "An error has occurred");
			console.error(error);
			return false;
		}
	}

	/**
	 * A static method used to convert an object to a Slot
	 * @param {Object} obj The object to convert
	 * @param {Boolean} includeNonStandardProperties Set to true if you want to include non standard properties in the result
	 * @static
	 */
	static fromObject({ destination, price, ...rest }, includeNonStandardProperties = false) {
		const options = { ...rest, includeNonStandardProperties };
		return new Slot(destination, price, options);
	}

	static get(id) {
		const allSlots = store.getState().slots;
		return allSlots.find(slot => slot.id === id);
	}

	/**
	 * @return {Array<Slot>}
	 */
	static getByDestination(destinationAirportId, onlyUnused = true) {
		const allSlots = store.getState().slots;
		return allSlots.filter(slot => {
			if (onlyUnused && slot.checkIfSlotUsed()) return false;
			if (slot.destination !== destinationAirportId) return false;
			return true;
		});
	}

	/**
	 * Returns an array with combined slots at same destinations. Omits used slots by default.
	 * @param {array<Slot>} currentSlots An array of current slots.
	 * @param {boolean} omitUsedSlots Omits used slots in the resulting array. True by default.
	 * @param {boolean} bothNumbers If true additional properties are provided in the result: usedAmount, unusedAmount. omitUsedSlots must be false!
	 * @returns {array<Slot>} An array of merged slots.
	 */
	static getMergedSlots(
		currentSlots,
		omitUsedSlots = true,
		bothNumbers = false,
		additionalUsedIds = [],
		additionalUnusedIds = []
	) {
		const allSlots = currentSlots || store.getState().slots;
		let usedSlots = Slot.getAllUsedSlotsIds();

		if (Array.isArray(additionalUsedIds)) {
			usedSlots.push(...additionalUsedIds);
		}

		if (Array.isArray(additionalUnusedIds)) {
			console.log(usedSlots);
			usedSlots = usedSlots.filter(s => !additionalUnusedIds.includes(s));
			console.log(usedSlots);

			// usedSlots = usedSlots.filter(s => !additionalUnusedIds.includes(s.id));
		}

		const mergedSlots = allSlots.reduce((newSlots, currSlot) => {
			if (omitUsedSlots && usedSlots.includes(currSlot.id)) return newSlots;
			const i = newSlots.findIndex(s => s.destination === currSlot.destination);
			const slotUsed = usedSlots.includes(currSlot.id);

			if (i > -1) {
				if (omitUsedSlots && newSlots[i].slotUsed) return newSlots;
				slotUsed ? newSlots[i].usedAmount++ : newSlots[i].unusedAmount++;
				newSlots[i].amount++;
				return newSlots;
			}

			currSlot.amount = 1;

			if (bothNumbers) {
				currSlot.usedAmount = slotUsed ? 1 : 0;
				currSlot.unusedAmount = slotUsed ? 0 : 1;
			}

			return [...newSlots, currSlot];
		}, []);

		return mergedSlots;
	}

	static getAllUsedSlotsIds() {
		const flights = store.getState().flights;
		return flights.map(f => f.slotId);
	}
}

export default Slot;
